"use client"

import { useMemo, useState } from "react"
import Link from "next/link"
import type { EmailTemplate, LexicalText } from "@/schema"
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ui/shad"
import { ExternalLink, FolderOpen, Loader2 } from "lucide-react"

import { useFetchEmailTemplates, useRenderEmailTemplate } from "@/hooks/api/email-templates"
import { ConfirmDialog } from "@/components/dialogs"

interface EmailTemplatePickerProps {
  householdId?: string
  hasContent: boolean
  onApply: (subject: string, body: LexicalText, attachments?: { signed_id: string; file_name: string }[]) => void
  skipRender?: boolean
  allowAttachments?: boolean
}

export function EmailTemplatePicker({
  householdId,
  hasContent,
  onApply,
  skipRender,
  allowAttachments = true,
}: EmailTemplatePickerProps) {
  const { data } = useFetchEmailTemplates()
  const renderTemplate = useRenderEmailTemplate()

  const [open, setOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingTemplate, setPendingTemplate] = useState<EmailTemplate | null>(null)

  const templates = useMemo(() => data?.email_templates ?? [], [data?.email_templates])
  const [search, setSearch] = useState("")

  const filteredTemplates = useMemo(() => {
    if (!search) return templates
    const q = search.toLowerCase()
    return templates.filter((t) => t.name?.toLowerCase().includes(q) || t.subject?.toLowerCase().includes(q))
  }, [templates, search])

  const applyTemplate = async (template: EmailTemplate) => {
    if (skipRender) {
      onApply(template.subject, template.body ?? { html: "" })
      setOpen(false)
      setPendingTemplate(null)
      return
    }

    try {
      const result = await renderTemplate.mutateAsync({
        emailTemplateId: template.id,
        householdId,
      })

      const rendered = result.email_template_render
      onApply(rendered.subject, rendered.body ?? { html: "" }, rendered.attachments)
      setOpen(false)
      setPendingTemplate(null)
    } catch {}
  }

  const handleSelect = (template: EmailTemplate) => {
    if (!allowAttachments && template.attachments?.length) return

    if (hasContent) {
      setPendingTemplate(template)
      setConfirmOpen(true)
    } else {
      applyTemplate(template)
    }
  }

  const handleConfirm = () => {
    if (pendingTemplate) {
      applyTemplate(pendingTemplate)
    }
    setConfirmOpen(false)
  }

  const hasTemplates = templates.length > 0

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button size="iconSm" variant="ghost" type="button" tooltip="Apply template">
            <FolderOpen className="text-icon" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start" side="top">
          {hasTemplates ? (
            <>
              <Command shouldFilter={false}>
                <CommandInput placeholder="Search templates..." value={search} onValueChange={setSearch} />
                <CommandList>
                  <CommandEmpty>No templates found</CommandEmpty>
                  <CommandGroup>
                    {filteredTemplates.map((template) => {
                      const attachmentsUnsupported = !allowAttachments && Boolean(template.attachments?.length)

                      return (
                        <CommandItem
                          key={template.id}
                          value={`${template.name} ${template.subject}`}
                          onSelect={() => handleSelect(template)}
                          disabled={renderTemplate.isPending || attachmentsUnsupported}
                          className="flex flex-col items-start gap-0.5 py-2"
                        >
                          <span className="font-medium">{template.name}</span>
                          <span className="text-muted-foreground text-xs">{template.subject}</span>
                          {attachmentsUnsupported && (
                            <span className="text-muted-foreground text-xs">
                              Attachments aren&apos;t supported for form invitations
                            </span>
                          )}
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
              {renderTemplate.isPending && (
                <div className="flex items-center justify-center border-t p-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-muted-foreground ml-2 text-sm">Applying template...</span>
                </div>
              )}
            </>
          ) : (
            <div className="p-3">
              <p className="text-muted-foreground mb-2 text-sm">No email templates yet.</p>
              <Link
                href="/settings/data-customizations/email-templates"
                className="text-primary hover:text-primary/80 inline-flex items-center gap-1.5 text-sm font-medium"
                onClick={() => setOpen(false)}
              >
                Create a template
                <ExternalLink className="size-3.5" />
              </Link>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <ConfirmDialog
        dismiss="explicit"
        title={"Replace email content?"}
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        description={
          "This will replace your current email subject and body with the template content. This action cannot be\n              undone."
        }
        action={{ label: "Replace", onClick: handleConfirm }}
        cancel={{ onClick: () => setPendingTemplate(null) }}
      ></ConfirmDialog>
    </>
  )
}
